import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { toast } from 'sonner';
import { Queue } from '@rossum/api-client/queues';
import { QueueRule } from '@rossum/api-client/schemas';
import { notify } from '../../toaster';
import { RULES_QUERY_KEY } from './rulesQueryKeys';
import { usePatchRule } from './usePatchRule';

type RemoveRuleParams = {
  rule: QueueRule;
  queue: Queue;
};

export const hasRemoveAction = (
  queue: Queue | undefined,
  rule: QueueRule
): queue is Queue => Boolean(queue && rule.queues.length > 1);

export const useRemoveRule = (onSuccess?: () => void) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { mutateAsync: patchRule } = usePatchRule();

  return useMutation({
    mutationFn: async ({ rule, queue }: RemoveRuleParams) => {
      if (!hasRemoveAction(queue, rule)) {
        throw new Error(
          'Cannot remove rule: rule must be linked to more than one queue'
        );
      }

      const updatedQueues = rule.queues.filter(q => q !== queue.url);

      return patchRule({
        patchedRuleId: rule.id,
        patchedRule: { queues: updatedQueues },
        skipInvalidation: true,
      });
    },
    onSuccess: (_, { rule }) => {
      queryClient.invalidateQueries([RULES_QUERY_KEY]);
      onSuccess?.();
      const toastId = `undo-remove-rule-${rule.id}`;
      notify.default({
        title: intl.formatMessage({
          id: 'features.queueSettings.rules.list.remove.notification.success',
        }),
        toastActions: [
          {
            label: intl.formatMessage({
              id: 'components.toaster.actions.undo',
            }),
            onClick: () => {
              toast.dismiss(toastId);
              patchRule(
                {
                  patchedRuleId: rule.id,
                  patchedRule: { queues: rule.queues },
                },
                {
                  onError: () =>
                    notify.error({
                      title: intl.formatMessage({
                        id: 'features.queueSettings.rules.list.remove.undo.notification.failure',
                      }),
                    }),
                }
              );
            },
          },
        ],
        id: toastId,
      });
    },
    onError: () =>
      notify.error({
        title: intl.formatMessage({
          id: 'features.queueSettings.rules.list.remove.notification.failure',
        }),
      }),
  });
};
